import Storage from 'disco-storage'
import get from 'lodash/get'
import { createReducer } from 'redux-create-reducer'
import type { EventSource } from 'types/ui'
import type { MediaTrackPlayAudioEventProperties } from 'types/media'
import {
  BACKGROUND_PLAYBACK_SERVICE_STATUS_SYNC,
  SHORTLIST_CLEAR,
  SHORTLIST_DELETE,
  TRACK_ACTIVATE,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_FORMAT_DELETE_SUCCESS,
  TRACK_MERGE_SUCCESS,
  TRACK_PAUSE,
  TRACK_PLAY,
  TRACK_MEDIA_PROPERTIES_UPDATE,
  TRACK_PLAY_PROGRESS_UPDATE,
  TRACK_PLAYLIST_PAGE_LOAD,
  TRACK_PLAYLIST_PAGE_LOAD_ERROR,
  TRACK_PLAYLIST_PAGE_LOAD_SUCCESS,
  TRACK_PRELOAD,
  TRACK_RESET,
  TRACK_SEEK,
  TRACK_STATUS_UPDATE,
  TRACK_STOP,
  TRACK_WAVEFORM_LOAD,
  TRACK_WAVEFORM_LOAD_ERROR,
  TRACK_WAVEFORM_LOAD_SUCCESS,
  TRACKS_UPLOADER_SAVE_SUCCESS,
  VOLUME_CHANGE,
} from '@disco/constants/ActionTypes'
import { LOADING, PAUSED, PLAYING, SEEKING } from '@disco/constants/MediaStatus'
export type TrackStatus = {
  loadStatus: string | null
  playbackStatus: string | null | undefined
  playbackPosition: number
  playbackLocation?: string | null | undefined
}

export type WaveformStatus = {
  waveformLoadStatus?: 'loading' | 'loaded' | 'error'
  waveformData?: {
    samples?: number[]
  }
}

export type State = {
  trackOriginalId: number | null | undefined
  trackGroupId: number | null | undefined
  trackPlaylistId: number | null | undefined
  trackShortlistId: string | null | undefined
  trackShareId: number | null | undefined
  trackCampaignId: number | null | undefined
  trackRecipientId: number | null | undefined
  trackMediaSource: EventSource | null
  trackPlaylistPageLoading: boolean
  clientLibraryId: number | null | undefined
  volume: number
  preloadingTrackId?: number
  tracklist: number[]
  trackStatus: Record<number, TrackStatus>
  trackWaveformStatus: Record<number, WaveformStatus>
  trackMediaProperties: Record<number, MediaTrackPlayAudioEventProperties>
}
const initialState: State = {
  trackOriginalId: null,
  trackGroupId: null,
  trackPlaylistId: null,
  trackShortlistId: null,
  trackShareId: null,
  trackCampaignId: null,
  trackRecipientId: null,
  clientLibraryId: null,
  trackMediaSource: null,
  trackPlaylistPageLoading: false,
  volume: typeof window !== 'undefined' ? Storage.getSync('volume') || 1 : 1,
  tracklist: [],
  trackStatus: {},
  trackWaveformStatus: {},
  trackMediaProperties: {},
}

// we shouldn't reset volume when resetting state
const resetState = state => ({ ...initialState, volume: state.volume })

const WAVEFORM_DATA_WIDTH = 1800
const WAVEFORM_DATA_HEIGHT = 140
const defaultWaveform = {
  width: WAVEFORM_DATA_WIDTH,
  height: WAVEFORM_DATA_HEIGHT,
  samples: Array(WAVEFORM_DATA_WIDTH).fill(
    Math.floor(WAVEFORM_DATA_HEIGHT / 40),
  ),
}
export const mediaReducer = createReducer<State, any>(initialState, {
  [TRACK_PLAY](
    state,
    {
      trackId,
      // TODO: replace with playbackContext
      playlistId,
      shortlistId,
      shareId,
      groupId,
      campaignId,
      recipientId,
      mediaSource,
      offlineMode,
      clientLibraryId,
      tracklist,
      playbackLocation,
    },
  ) {
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          loadStatus: get(state.trackStatus[trackId], 'loadStatus', LOADING),
          playbackStatus: PLAYING,
          playbackPosition: get(
            state.trackStatus[trackId],
            'playbackPosition',
            0,
          ),
          playbackLocation: playbackLocation || null,
        },
      },
      trackWaveformStatus: {
        ...state.trackWaveformStatus,
        [trackId]: {
          waveformData: defaultWaveform,
          ...state.trackWaveformStatus[trackId], // show default waveform during loading
        },
      },
      trackOriginalId: trackId,
      trackPlaylistId: playlistId || null,
      tracklist: tracklist || [],
      trackShareId: shareId,
      trackGroupId: groupId,
      trackRecipientId: recipientId,
      trackCampaignId: campaignId,
      trackMediaSource: mediaSource,
      trackPlaylistPageLoading:
        playlistId === state.trackPlaylistId
          ? state.trackPlaylistPageLoading
          : false,
      trackShortlistId: shortlistId || null,
      clientLibraryId,
      trackOffline: offlineMode,
      trackMediaProperties: {},
    }
  },

  [TRACK_MEDIA_PROPERTIES_UPDATE](state, { payload }) {
    return {
      ...state,
      trackMediaProperties: {
        // Only present properties for playing track to avoid stale data for other tracks
        [payload.trackId]: { ...payload.properties },
      },
    }
  },

  [TRACK_PRELOAD](
    state,
    {
      trackId,
      // TODO: replace with playbackContext
      playlistId,
      shortlistId,
      shareId,
      groupId,
      campaignId,
      recipientId,
      mediaSource,
      offlineMode,
      clientLibraryId,
      tracklist,
    },
  ) {
    return {
      ...state,
      preloadingTrackId: trackId,
      trackPlaylistId: playlistId,
      // TODO: refactor actions
      tracklist: tracklist || state.tracklist || [],
      trackShareId: shareId,
      trackGroupId: groupId,
      trackRecipientId: recipientId,
      trackCampaignId: campaignId,
      trackMediaSource: mediaSource,
      trackPlaylistPageLoading:
        playlistId === state.trackPlaylistId
          ? state.trackPlaylistPageLoading
          : false,
      trackShortlistId: shortlistId || null,
      clientLibraryId,
      trackOffline: offlineMode,
    }
  },

  [TRACK_ACTIVATE](state, { trackId, playlistId, mediaSource }) {
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          loadStatus: null,
          playbackStatus: null,
          playbackPosition: 0,
        },
      },
      trackOriginalId: trackId,
      trackPlaylistId: playlistId || null,
      trackMediaSource: mediaSource,
      trackMediaProperties: {},
    }
  },

  [BACKGROUND_PLAYBACK_SERVICE_STATUS_SYNC](
    state,
    { trackId, playbackStatus },
  ) {
    const id = trackId ? trackId : state.trackOriginalId
    if (!id) return state
    return {
      ...state,
      trackOriginalId: id,
      trackStatus: {
        [id]: {
          ...(state.trackStatus?.[id] ?? {}),
          loadStatus: null,
          playbackStatus: playbackStatus || null,
          playbackPosition: 0,
        },
      },
    }
  },

  [TRACK_PAUSE](state, { trackId }) {
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          loadStatus: null,
          playbackPosition: 0,
          ...state.trackStatus[trackId],
          playbackStatus: PAUSED,
        },
      },
    }
  },

  [TRACK_SEEK](state, { trackId, position, location }) {
    const existing = state.trackStatus[trackId]
    return {
      ...state,
      trackStatus: {
        ...state.trackStatus,
        [trackId]: {
          loadStatus: existing?.loadStatus ?? null,
          playbackPosition: position,
          playbackStatus: SEEKING,
          // Preserve the location set at play time (used for the Seek audio
          // event's Context) when the seek itself doesn't specify one --
          // most seekTrackPosition() callers don't pass a location.
          playbackLocation: location ?? existing?.playbackLocation,
        },
      },
    }
  },

  [TRACK_STOP](state, { trackId }) {
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          loadStatus: null,
          playbackStatus: null,
          playbackPosition: 0,
        },
      },
    }
  },

  [TRACK_STATUS_UPDATE](state, { trackId, loadStatus, playbackStatus }) {
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          playbackPosition: 0,
          ...state.trackStatus[trackId],
          loadStatus: loadStatus,
          playbackStatus: playbackStatus,
        },
      },
    }
  },

  [TRACK_PLAY_PROGRESS_UPDATE](state, { trackId, progress }) {
    // if deferred progress update arrive when status no longer exists or track
    // have stopped, just return current state
    if (
      !state.trackStatus[trackId] ||
      !state.trackStatus[trackId].playbackStatus
    )
      return state
    return {
      ...state,
      trackStatus: {
        [trackId]: {
          ...state.trackStatus[trackId],
          playbackPosition: progress,
        },
      },
    }
  },

  [TRACK_FORMAT_DELETE_SUCCESS](state, { track }) {
    if (track.id === state.trackOriginalId) return resetState(state)
    return state
  },

  [TRACKS_UPLOADER_SAVE_SUCCESS](state, { track }) {
    if (track.id === state.trackOriginalId) return resetState(state)
    return state
  },

  [TRACK_DELETE_BATCH_SUCCESS](state, { trackIds }) {
    if (trackIds.indexOf(state.trackOriginalId) > -1) return resetState(state)
    return state
  },

  [TRACK_MERGE_SUCCESS](state, { tracksToMerge }) {
    const trackIds = tracksToMerge.map(t => t.id)
    if (trackIds.indexOf(state.trackOriginalId) > -1) return resetState(state)
    return state
  },

  [TRACK_PLAYLIST_PAGE_LOAD](state) {
    return { ...state, trackPlaylistPageLoading: true }
  },

  [TRACK_PLAYLIST_PAGE_LOAD_SUCCESS](state) {
    return { ...state, trackPlaylistPageLoading: false }
  },

  [TRACK_PLAYLIST_PAGE_LOAD_ERROR](state) {
    return { ...state, trackPlaylistPageLoading: false }
  },

  [TRACK_WAVEFORM_LOAD](state, { trackId }) {
    return {
      ...state,
      trackWaveformStatus: {
        ...state.trackWaveformStatus,
        [trackId]: {
          ...state.trackWaveformStatus[trackId],
          waveformLoadStatus: 'loading',
        },
      },
    }
  },

  [TRACK_WAVEFORM_LOAD_SUCCESS](state, { trackId, waveform }) {
    return {
      ...state,
      trackWaveformStatus: {
        ...state.trackWaveformStatus,
        [trackId]: {
          ...state.trackWaveformStatus[trackId],
          waveformLoadStatus: 'loaded',
          waveformData: waveform,
        },
      },
    }
  },

  [TRACK_WAVEFORM_LOAD_ERROR](state, { trackId }) {
    return {
      ...state,
      trackWaveformStatus: {
        ...state.trackWaveformStatus,
        [trackId]: {
          ...state.trackWaveformStatus[trackId],
          waveformLoadStatus: 'error',
          waveformData: defaultWaveform,
        },
      },
    }
  },

  [TRACK_RESET](state) {
    return resetState(state)
  },

  [VOLUME_CHANGE](state, { volume }) {
    return { ...state, volume }
  },

  [SHORTLIST_CLEAR](state, { shortlistId }) {
    if (state.trackShortlistId === shortlistId) return resetState(state)
    return state
  },

  [SHORTLIST_DELETE](state, { shortlistId }) {
    if (state.trackShortlistId === shortlistId) return resetState(state)
    return state
  },
})
